import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircleIcon, PaperPlaneTiltIcon, ChatCircleDotsIcon } from '@phosphor-icons/react';
import { ApiError, Button, Input, Label, StatusChip } from '@smena/ui';
import { fetchIntegrationsHealth, type ChannelStatus } from '@/features/integrations/api';
import { deleteChannel, fetchChannels, upsertChannel, type ChannelName } from './api';

/**
 * Выбор бота для гостей - отдельно по Telegram и по MAX.
 *
 * Главное правило: общий бот Smena работает у клиента СРАЗУ и не требует ни одного нажатия.
 * Раньше шаг показывал «Ничего не подключено» и кнопку «Использовать общий», хотя гостевой
 * бот на контуре уже работал: строки в channel_configs просто не было, а её отсутствие и
 * значит «общий». Человек видел ложную тревогу и жал кнопку, которая ничего не меняла.
 *
 * MAX здесь раньше отсутствовал вовсе, хотя ручки `/api/staff/channels` принимают его наравне
 * с Telegram, а гостевой MAX-бот на контуре поднят.
 */
const CHANNELS: Array<{
  channel: ChannelName;
  title: string;
  statusKind: ChannelStatus['kind'];
  icon: React.ReactNode;
  botFatherHint: string;
}> = [
  {
    channel: 'telegram',
    title: 'Telegram',
    statusKind: 'telegram_guest',
    icon: <PaperPlaneTiltIcon weight="bold" className="size-4 text-accent-fg" />,
    botFatherHint: 'Создайте бота у @BotFather и вставьте токен.',
  },
  {
    channel: 'max',
    title: 'MAX',
    statusKind: 'max',
    icon: <ChatCircleDotsIcon weight="bold" className="size-4 text-accent-fg" />,
    botFatherHint: 'Создайте бота в MAX и вставьте токен из настроек бота.',
  },
];

export function GuestChannelChoice({ whitelabelEnabled }: { whitelabelEnabled: boolean }) {
  const healthQ = useQuery({ queryKey: ['integrations-health'], queryFn: fetchIntegrationsHealth });
  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: fetchChannels });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted">
        Гость получает ссылку на бот вместе с приглашением и попадает сразу в диалог.
        Общий бот Smena уже включён - если он вас устраивает, ничего нажимать не нужно, просто
        идите дальше.
      </p>

      {CHANNELS.map((c) => (
        <ChannelRow
          key={c.channel}
          spec={c}
          whitelabelEnabled={whitelabelEnabled}
          live={healthQ.data?.channels.find((s) => s.kind === c.statusKind)}
          custom={channelsQ.data?.items.find((i) => i.channel === c.channel && i.kind === 'custom')}
        />
      ))}
    </div>
  );
}

function ChannelRow({
  spec,
  whitelabelEnabled,
  live,
  custom,
}: {
  spec: (typeof CHANNELS)[number];
  whitelabelEnabled: boolean;
  live: ChannelStatus | undefined;
  custom: { botUsername: string | null; hasBotToken: boolean; lastError: string | null } | undefined;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['channels'] });
    void qc.invalidateQueries({ queryKey: ['integrations-health'] });
  }

  const connectMut = useMutation({
    mutationFn: () =>
      upsertChannel({
        channel: spec.channel,
        kind: 'custom',
        botToken: token.trim(),
        botUsername: username.trim() || undefined,
      }),
    onSuccess: () => {
      setToken('');
      setUsername('');
      setOpen(false);
      toast.success(`Ваш ${spec.title}-бот подключён`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось подключить бота'),
  });

  const revertMut = useMutation({
    mutationFn: () => deleteChannel(spec.channel),
    onSuccess: () => {
      toast.success(`Вернули общий бот Smena для ${spec.title}`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось переключить'),
  });

  const isCustom = Boolean(custom);
  // Имя бота берём живым (getMe / getMyInfo): на каждом контуре он свой, и клиент должен
  // видеть именно тот бот, которому будут писать его гости.
  const botLine = live?.botName
    ? `${live.botName}${live.botUsername ? ` · @${live.botUsername}` : ''}`
    : null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {spec.icon}
        <span className="text-sm font-semibold">{spec.title}</span>
        {isCustom ? (
          <StatusChip tone="green">ваш бот</StatusChip>
        ) : (
          <StatusChip tone="green">
            <CheckCircleIcon weight="fill" className="mr-1 inline size-3" />
            общий бот Smena · включён
          </StatusChip>
        )}
      </div>

      {isCustom ? (
        <div className="text-xs text-muted">
          {custom?.botUsername ? `@${custom.botUsername}` : 'токен сохранён'} ·{' '}
          {custom?.hasBotToken ? 'токен зашифрован' : 'без токена'}
        </div>
      ) : botLine ? (
        <div className="text-xs text-muted">
          Гости пишут боту <span className="text-text">{botLine}</span>
          {live?.botUrl ? (
            <>
              {' · '}
              <a
                href={live.botUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-fg underline decoration-dotted underline-offset-2"
              >
                открыть
              </a>
            </>
          ) : null}
        </div>
      ) : (
        <div className="text-xs text-muted">
          Бот этого контура пока не отвечает - имя появится, когда он поднимется.
        </div>
      )}

      {custom?.lastError ? <div className="text-xs text-red">{custom.lastError}</div> : null}

      {isCustom ? (
        <div>
          <Button size="sm" variant="ghost" disabled={revertMut.isPending} onClick={() => revertMut.mutate()}>
            {revertMut.isPending ? 'Возвращаем…' : 'Вернуть общий бот Smena'}
          </Button>
        </div>
      ) : !open ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
            Подключить свой бот
          </Button>
          {!whitelabelEnabled ? <StatusChip tone="amber">white-label · доплата</StatusChip> : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2 border-t border-line pt-2">
          {!whitelabelEnabled ? (
            <p className="text-xs text-amber">
              Свой брендированный бот - платная опция сверх тарифа. Напишите в поддержку, чтобы
              её включили; после активации поля здесь станут активными.
            </p>
          ) : (
            <p className="text-xs text-muted">{spec.botFatherHint}</p>
          )}
          <Label htmlFor={`${spec.channel}-token`}>Токен бота</Label>
          <Input
            id={`${spec.channel}-token`}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={whitelabelEnabled ? 'токен бота' : 'доступно после активации white-label'}
            disabled={!whitelabelEnabled}
            autoComplete="off"
          />
          <Label htmlFor={`${spec.channel}-username`}>Username бота без @</Label>
          <Input
            id={`${spec.channel}-username`}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="my_uk_bot"
            disabled={!whitelabelEnabled}
            autoComplete="off"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!whitelabelEnabled || !token.trim() || connectMut.isPending}
              onClick={() => connectMut.mutate()}
            >
              {connectMut.isPending ? 'Подключаем…' : 'Подключить'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
